import { useParams, Link } from 'react-router-dom'
import { useAdsetData } from './hooks/useAdsetData'
import { ArrowRightIcon, CalendarIcon, HistoryIcon } from '@/components/icons'
import ChevronIcon from '@/components/icons/ChevronIcon'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Button } from '@/components/ui/button'
import { Table, TableHeader, TableBody, TableHead, TableRow, TableCell } from '@/components/ui/table'
import { cn } from '@/lib/utils'

const getColorForValue = (value, metric) => {
    // Define thresholds based on metric type
    const thresholds = {
        roas: { good: 1.0, medium: 0.7 },
        revenue: { good: 4.0, medium: 2.5 },
        cpm: { good: 2.5, medium: 3.0 }, // Lower is better
        costPerPurchase: { good: 3.0, medium: 3.4 }, // Lower is better
    }

    const threshold = thresholds[metric]
    if (!threshold) return 'tw:bg-gray-700'

    // For metrics where lower is better (CPM, Cost per Purchase)
    if (metric === 'cpm' || metric === 'costPerPurchase') {
        if (value <= threshold.good) return 'tw:bg-green-900/50'
        if (value <= threshold.medium) return 'tw:bg-yellow-900/40'
        return 'tw:bg-red-900/50'
    }

    // For metrics where higher is better (ROAS, Revenue)
    if (value >= threshold.good) return 'tw:bg-green-900/50'
    if (value >= threshold.medium) return 'tw:bg-yellow-900/40'
    return 'tw:bg-red-900/50'
}

const MetricsTable = ({ data }) => {
    return (
        <div className="tw:mt-6">
            <Table>
                <TableHeader>
                    <TableRow className="tw:bg-gray-900">
                        <TableHead className="tw:text-left tw:p-4 tw:text-white tw:bg-primary-background">
                            Key Metrics
                        </TableHead>
                        {data.dates.map((date) => (
                            <TableHead
                                key={date}
                                className="tw:text-center tw:p-4 tw:text-white tw:bg-primary-background"
                            >
                                {date}
                            </TableHead>
                        ))}
                        <TableHead className="tw:text-center tw:p-4 tw:text-white tw:bg-primary-background">
                            Average
                        </TableHead>
                        <TableHead className="tw:text-center tw:p-4 tw:text-white tw:bg-primary-background">
                            Average
                        </TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {data.metrics.map((metric, rowIndex) => (
                        <TableRow key={metric.name} className="tw:!max-h-[38px]">
                            <TableCell
                                className={cn(
                                    'tw:p-4 tw:text-white tw:font-medium tw:!max-h-[38px]',
                                    rowIndex % 2 === 0 ? 'tw:bg-[#2B3B4C]' : 'tw:bg-primary-background',
                                )}
                            >
                                {metric.label}
                            </TableCell>
                            {metric.values.map((value, index) => (
                                <TableCell
                                    key={index}
                                    className={cn(
                                        'tw:text-center tw:p-4 tw:text-white',
                                        getColorForValue(value, metric.type),
                                    )}
                                >
                                    {metric.format === 'currency' ? `$${value.toFixed(2)}` : value.toFixed(2)}
                                </TableCell>
                            ))}
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </div>
    )
}

const AdsetStoryline = () => {
    const { id: campaignId, adSetId } = useParams()
    const { adset, loading, error } = useAdsetData(campaignId, adSetId)

    // Sample data matching the image
    const metricsData = {
        dates: ['Tue Aug 5', 'Wed Aug 6', 'Thu Aug 7', 'Fri Aug 8', 'Sat Aug 9', 'Sun Aug 10', 'Mon Aug 11'],
        metrics: [
            {
                name: 'roas',
                label: 'ROAS (All)',
                type: 'roas',
                format: 'number',
                values: [0.79, 0.47, 0.32, 0.87, 1.05, 1.21, 1.08],
                average: 0.85,
                overallAverage: 0.82,
            },
            {
                name: 'spend',
                label: 'Amount Spent',
                type: 'spend',
                format: 'currency',
                values: [17.02, 16.21, 18.16, 19.61, 14.64, 12.25, 11.87],
                average: 15.25,
                overallAverage: 14.19,
            },
            {
                name: 'revenue',
                label: 'Revenue',
                type: 'revenue',
                format: 'currency',
                values: [3.98, 4.08, 3.12, 1.42, 2.61, 3.12, 5.94],
                average: 5.12,
                overallAverage: 4.29,
            },
            {
                name: 'costPerPurchase',
                label: 'Cost per Web Purchase',
                type: 'costPerPurchase',
                format: 'currency',
                values: [3.12, 3.17, 3.53, 3.59, 3.53, 3.17, 2.58],
                average: 3.29,
                overallAverage: 3.21,
            },
            {
                name: 'cpm',
                label: 'CPM',
                type: 'cpm',
                format: 'currency',
                values: [2.02, 2.18, 3.54, 3.61, 2.54, 2.18, 2.02],
                average: 2.2,
                overallAverage: 2.21,
            },
        ],
    }

    if (loading) {
        return (
            <div className="tw:p-6">
                <div className="tw:text-white">Loading adset...</div>
            </div>
        )
    }

    if (error) {
        return (
            <div className="tw:p-6">
                <div className="tw:text-red-500">Error loading adset: {error.message}</div>
            </div>
        )
    }

    return (
        <div>
            <div className="tw:px-6 tw:py-4 tw:border-b">
                <div className="tw:flex tw:gap-4">
                    <div className="tw:flex tw:gap-1 tw:items-center">
                        <HistoryIcon className="tw:w-8 tw:h-8 tw:text-gray-200" />
                        <span className="tw:text-xl tw:font-medium">Ad Set Storyline</span>
                    </div>
                    <Link
                        to={`/campaigns/media-buying/${campaignId}/ads-manager`}
                        className="tw:flex tw:items-center tw:gap-1 tw:text-sm tw:font-medium"
                    >
                        <ArrowRightIcon className="tw:rotate-180 tw:h-6 tw:w-6" /> Back to Ads Manager
                    </Link>
                </div>
            </div>
            <div>
                <div className="tw:p-6">
                    <div className="tw:flex tw:items-center tw:justify-between tw:gap-4">
                        <div className="tw:flex tw:items-center tw:gap-4 tw:w-full">
                            <Select className="tw:w-full">
                                <SelectTrigger className="tw:w-full">
                                    <SelectValue placeholder="Acquisition" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="acquisition">Acquisition</SelectItem>
                                    <SelectItem value="retention">Retention</SelectItem>
                                    <SelectItem value="conversion">Conversion</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <Button variant="link" className="tw:flex tw:items-center tw:gap-1 tw:text-blue-300">
                            <CalendarIcon />
                            <span>1 Sep, 2025 – 4 Sep, 2025</span>
                        </Button>
                    </div>
                    <div className="tw:flex tw:items-center tw:justify-between tw:pt-4">
                        <div className="tw:flex tw:gap-2">
                            <Button variant="secondary" size="default" className="tw:w-[164px]">
                                <ChevronIcon className="tw:min-w-5 tw:min-h-5 tw:rotate-90" />
                                Previous Ad Set
                            </Button>
                            <Button variant="secondary" size="default" className="tw:w-[164px]">
                                Next Ad Set
                                <ChevronIcon className="tw:min-w-5 tw:min-h-5 tw:-rotate-90" />
                            </Button>
                        </div>
                        <div className="tw:flex tw:gap-2">
                            <Button variant="secondary" size="default" className="tw:w-[164px]">
                                <ChevronIcon className="tw:min-w-5 tw:min-h-5 tw:rotate-90" />
                                Previous Week
                            </Button>
                            <Button variant="secondary" size="default" className="tw:w-[164px]">
                                Next Week
                                <ChevronIcon className="tw:min-w-5 tw:min-h-5 tw:-rotate-90" />
                            </Button>
                        </div>
                    </div>
                </div>

                <MetricsTable data={metricsData} />
            </div>
        </div>
    )
}

export default AdsetStoryline
